import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AiInfoSheet,
  BankAvatar,
  Box,
  Button,
  FeatureGate,
  Icon,
  Screen,
  Text,
} from '~/atomic';
import modalStatusService from '~/services/modalStatus';
import { cn } from '~/utils/cn';
import { formatCentsToBRL } from '~/utils/formatFinance';
import { useAppSelector } from '~/store';
import {
  selectContractsRecent,
} from '~/store/contracts/selectors';
import { useFeature } from '~/store/entitlements/useFeature';
import type { Contract } from '~/store/contracts/interfaces';

type Mode = 'ai' | 'manual';

const MAX_FILE_SIZE = 32 * 1024 * 1024;
const ACCEPTED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const ACCEPTED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];

function showUploadError(title: string, description: string) {
  modalStatusService.show({
    type: 'error',
    title,
    description,
    showFirstButton: true,
    textFirstButton: 'Entendi',
    onClickFirstButton: () => modalStatusService.hide(),
    handleCloseModal: () => modalStatusService.hide(),
  });
}

function ModeTab({
  active,
  icon,
  label,
  onClick,
  disabled,
}: {
  active: boolean;
  icon: 'icon-ai' | 'icon-pencil';
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  const iconColor = active ? '#FFFFFF' : disabled ? '#B0B0B0' : '#606060';
  const textClass = active
    ? 'color-intense-01'
    : disabled
      ? 'color-intense-04'
      : 'color-intense-06';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex-1 h-[56px] flex items-center justify-center gap-nano rounded-small border transition-colors',
        active
          ? 'bg-primary-01 border-primary-01'
          : disabled
            ? 'bg-intense-02 border-intense-03'
            : 'bg-intense-01 border-intense-03 hover:bg-intense-02',
      )}
    >
      <Icon name={icon} size={18} color={iconColor} rounded={false} />
      <Text className={cn('text-paragraphMD', textClass)}>{label}</Text>
    </button>
  );
}

function RecentContractCard({
  contract,
  onClick,
}: {
  contract: Contract;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-smaller w-full flex items-center gap-smaller rounded-small bg-intense-01 border border-intense-03 p-small hover:bg-intense-02 text-left"
    >
      <BankAvatar institution={contract.institution} size="md" />
      <div className="flex-1 min-w-0">
        <Text className="text-bodyLG color-complement-01" numberOfLines={1}>
          {contract.institution.name}
        </Text>
        <Text className="mt-quark text-captionMD color-intense-06" numberOfLines={1}>
          {contract.cropDescription || `Contrato ${contract.contractNumber}`}
        </Text>
      </div>
      <div className="flex flex-col items-end gap-quark">
        <Text className="text-bodyMD color-complement-01">
          {formatCentsToBRL(contract.amountRemaining)}
        </Text>
        <Icon name="icon-arrow-right" size={16} color="#606060" rounded={false} />
      </div>
    </button>
  );
}

export default function AddArchiveView() {
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('ai');
  const [showInfo, setShowInfo] = useState(false);
  const recentContracts = useAppSelector(selectContractsRecent);
  const pdf = useFeature('pdf_extraction');

  const handleUploadFile = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const typeOk =
      ACCEPTED_MIME_TYPES.includes(file.type) ||
      ACCEPTED_EXTENSIONS.includes(ext);

    if (!typeOk) {
      showUploadError(
        'Formato não suportado',
        'Envie o contrato em PDF, JPG ou PNG.',
      );
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      const mb = (file.size / 1024 / 1024).toFixed(1).replace('.', ',');
      showUploadError(
        'Arquivo muito grande',
        `O arquivo tem ${mb} MB.\nO limite é 32 MB.`,
      );
      return;
    }

    navigate('/app/contracts/analyzing', {
      state: {
        file: {
          name: file.name,
          size: file.size,
          type: file.type,
        },
      },
    });
    (window as unknown as { __pagaagroPendingFile?: File }).__pagaagroPendingFile = file;
  };

  return (
    <Screen showHeader={false} hasTabNavigation bgClassName="bg-intense-02">
      <Box className="py-medium">
        <Text className="text-headingH1 color-complement-01" as="h1">
          Novo contrato
        </Text>
        <Text className="mt-quark text-bodySM color-intense-06">
          Cadastre um financiamento
        </Text>

        <div className="mt-medium flex gap-smaller">
          <ModeTab
            active={mode === 'ai'}
            icon="icon-ai"
            label="Enviar PDF"
            onClick={() => setMode('ai')}
          />
          <ModeTab
            active={mode === 'manual'}
            icon="icon-pencil"
            label="Manual"
            onClick={() => setMode('manual')}
          />
        </div>

        {mode === 'ai' && (
          <Box className="mt-medium">
            <FeatureGate feature="pdf_extraction">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="w-full flex flex-col items-center justify-center rounded-medium bg-success-01 border-2 border-dashed border-success-02 py-larger px-small hover:bg-success-02/40 transition-colors"
              >
                <div className="w-[56px] h-[56px] rounded-full bg-intense-01 flex items-center justify-center">
                  <Icon name="icon-arrow-up" size={20} color="#0E0D0D" rounded={false} />
                </div>
                <Text className="mt-smaller text-paragraphLG color-complement-01">
                  Envie o contrato
                </Text>
                <Text className="mt-quark text-bodySM color-intense-06">
                  PDF, JPG ou PNG até 32MB
                </Text>
                {pdf.state === 'freemium' && pdf.quotaPerMonth !== null && (
                  <Text className="mt-smaller text-caption color-primary-01">
                    {pdf.quotaRemaining ?? 0} de {pdf.quotaPerMonth} extrações restantes este mês
                  </Text>
                )}
              </button>
            </FeatureGate>

            {recentContracts.length > 0 && (
              <Box className="mt-medium">
                <Text className="text-bodyMD color-intense-06">Contratos recentes</Text>
                {recentContracts.map((c) => (
                  <RecentContractCard
                    key={c.id}
                    contract={c}
                    onClick={() => navigate(`/app/contracts/${c.id}`)}
                  />
                ))}
              </Box>
            )}

            <button
              type="button"
              onClick={() => setShowInfo(true)}
              className="mt-medium w-full flex items-start gap-smaller rounded-small bg-success-01 p-small hover:bg-success-02/40 text-left"
            >
              <div className="w-[40px] h-[40px] rounded-full bg-success-02 flex items-center justify-center shrink-0">
                <Icon name="icon-ai" size={20} color="#1D4733" rounded={false} />
              </div>
              <div className="flex-1">
                <Text className="text-paragraphLG color-complement-01">Como funciona</Text>
                <Text className="mt-quark text-bodySM color-intense-06">
                  Lemos o contrato e preenchemos prazo, taxa, parcelas e indexador. Você
                  revisa antes de salvar.
                </Text>
              </div>
              <Icon name="icon-arrow-right" size={18} color="#606060" rounded={false} />
            </button>
          </Box>
        )}

        {mode === 'manual' && (
          <Box className="mt-medium">
            <FeatureGate feature="contracts_basic">
              <Box className="rounded-medium bg-success-01 p-medium flex flex-col items-center text-center">
                <div className="w-[56px] h-[56px] rounded-full bg-success-02 flex items-center justify-center">
                  <Icon name="icon-pencil" size={22} color="#1D4733" rounded={false} />
                </div>
                <Text className="mt-smaller text-paragraphLG color-complement-01">
                  Cadastro manual
                </Text>
                <Text className="mt-quark text-bodySM color-intense-06">
                  Preencha os dados do contrato você mesmo. Você poderá revisar e ajustar
                  tudo antes de salvar.
                </Text>
              </Box>

              {recentContracts.length > 0 && (
                <Box className="mt-medium">
                  <Text className="text-bodyMD color-intense-06">Contratos recentes</Text>
                  {recentContracts.map((c) => (
                    <RecentContractCard
                      key={c.id}
                      contract={c}
                      onClick={() => navigate(`/app/contracts/${c.id}`)}
                    />
                  ))}
                </Box>
              )}

              <Box className="mt-medium">
                <Button
                  text="Preencher dados do contrato"
                  rightIcon="icon-arrow-right"
                  onClick={() => navigate('/app/contracts/review', { state: { mode: 'manual' } })}
                  fullWidth
                />
              </Box>
            </FeatureGate>
          </Box>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleUploadFile(file);
            e.target.value = '';
          }}
        />
      </Box>

      <AiInfoSheet open={showInfo} onClose={() => setShowInfo(false)} />
    </Screen>
  );
}
